import { HashRouter as Router, Route, Routes, Navigate } from "react-router-dom";

import "./App.css";
import "./components/SubmitJobs/ImportForm.css";
import JobSubmissionForm from "./components/SubmitJobs/ImportForm";
import { Fade, ThemeProvider, createTheme } from "@mui/material";
import React, { useEffect } from "react";
import { CustomToolbar, CustomTitle, DrawerNavigation, NavLink } from "./components/Toolbar";
import { LoginForm, Logout, Register, PasswordReset, RegistrationContactNotice } from "./components/authentication";
import { UserContext } from "./hooks/UserContext";
import { getJwtToken, setJwtToken, setRefreshToken } from "./utilities/utils";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CompanyContextProvider } from "./hooks/CompanyContext";
import JobsDashboardWithLocalizationProvider from "./components/JobsDashboard/JobsDashboard";
import { MarkJobReadyPage } from "./components/MarkJobsReady/MarkJobReadyPage";
import * as Sentry from "@sentry/react";
import { LoadingComponent } from "./components/helpers";
import HomePage from "./components/Home";
import { API } from "./api";
import { DEFAULT_PAGE } from "./utilities/constants";
import { User } from "./models";
import {
	AgrianIssues,
	PostSamplingIssues,
	GeoFileConverter,
	ImportSettings,
	MissionControlLogs,
	MissionControlPasswordsGenerator,
	PasswordGenerator,
	SortShapefiles,
	UnmatchedDeals,
} from "./components/admin";

const theme = createTheme({
	palette: {
		primary: {
			main: "#274052",
		},
		secondary: {
			main: "#dc004e",
		},
	},
});

interface ProtectedRouteProps {
	redirectPath?: string;
}
const ProtectedRoute = ({ children, redirectPath }: React.PropsWithChildren<ProtectedRouteProps>) => {
	const jwtToken = getJwtToken();
	if (!jwtToken) {
		return <Navigate to={redirectPath ?? "/login"} replace />;
	}

	return children;
};

function App() {
	const [drawerOpen, setDrawerOpen] = React.useState(false);
	// force this to be non-null because we won't use this until our loading screen is done
	// therefore any passed down will certainly be truthy
	const [currentUser, setCurrentUser] = React.useState<User | undefined>();
	const [loading, setLoading] = React.useState(true);
	const [currentLink, setCurrentLink] = React.useState<NavLink>({ to: "/login", text: "Login" });

	useEffect(() => {
		const token = getJwtToken();
		if (!token) {
			setLoading(false);
		} else if (!currentUser?.id) {
			const fetchUser = async () => {
				try {
					const user = await API.Users.me();
					setCurrentUser(user);
				} catch (error: any) {
					// Clear tokens and user state on authentication failure
					if (error && (error.status === 401 || error.statusCode === 401)) {
						setJwtToken("");
						setRefreshToken("");
						setCurrentUser(undefined);
					}
				} finally {
					setLoading(false);
				}
			};
			fetchUser();
		} else {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!currentUser) {
			return;
		}
		Sentry.setUser({
			id: currentUser.id,
			email: currentUser.email,
			username: currentUser.fullName,
		});
	}, [currentUser]);

	const handleLogin = (user: User) => {
		setCurrentUser(user);
		setDrawerOpen(false); // Close the drawer after login
	};

	return loading ? (
		<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
			<LoadingComponent />
		</div>
	) : (
		<Fade in={!loading}>
			<div>
				<UserContext.Provider value={currentUser}>
					<ThemeProvider theme={theme}>
						<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
							<CompanyContextProvider user={currentUser}>
								<CustomTitle setCurrentLink={setCurrentLink} />
								<div>
									{/* Drawer navigation */}
									<CustomToolbar
										drawerOpen={drawerOpen}
										setDrawerVisibility={(open: boolean) => setDrawerOpen(open)}
										title={currentLink.text}
									/>
									<DrawerNavigation
										open={drawerOpen}
										onClose={() => setDrawerOpen(false)}
										setNavigationDrawerState={(open: boolean) => setDrawerOpen(open)}
										onNavigate={(link: NavLink) => {
											setCurrentLink(link);
											setDrawerOpen(false);
										}}
									/>

									{/* <div style={{ marginLeft: drawerOpen ? '250px' : '0', transition: 'margin-left 0.3s' }}> */}
									<div>
										{/* Drawer toggle button */}
										<Routes>
											<Route path="/home" element={<HomePage setLoggedIn={handleLogin} />} />
											<Route path="/login" element={<LoginForm setLoggedIn={handleLogin} />} />
											<Route path="/register" element={<Register setLoggedIn={handleLogin} />} />
											<Route
												path="/"
												element={currentUser ? <Navigate to={DEFAULT_PAGE} /> : <LoginForm setLoggedIn={handleLogin} />}
											/>
											<Route path="/forgot-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
											<Route path="/reset-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
											<Route path="/jobs">
												<Route
													index
													path="dashboard"
													element={
														<ProtectedRoute>
															<JobsDashboardWithLocalizationProvider setLoading={setLoading} />
														</ProtectedRoute>
													}
												/>
												<Route
													path="submit"
													element={
														<ProtectedRoute>
															<JobSubmissionForm />
														</ProtectedRoute>
													}
												/>
												<Route
													path="ready"
													element={
														<ProtectedRoute>
															<MarkJobReadyPage setLoading={setLoading} />
														</ProtectedRoute>
													}
												/>
											</Route>
											<Route path="/logout" element={<Logout />} />
											<Route path="/registration-notice" element={<RegistrationContactNotice />} />
											<Route path="/admin">
												<Route
													index
													path="sortshp"
													element={
														<ProtectedRoute>
															<SortShapefiles />
														</ProtectedRoute>
													}
												/>
												<Route
													path="import"
													element={
														<ProtectedRoute>
															<ImportSettings />
														</ProtectedRoute>
													}
												/>
												<Route
													path="geofile-converter"
													element={
														<ProtectedRoute>
															<GeoFileConverter />
														</ProtectedRoute>
													}
												/>
												<Route
													path="mission-control-logs"
													element={
														<ProtectedRoute>
															<MissionControlLogs />
														</ProtectedRoute>
													}
												/>
												<Route
													path="mission-control-passwords-generator"
													element={
														<ProtectedRoute>
															<MissionControlPasswordsGenerator />
														</ProtectedRoute>
													}
												/>
												<Route
													path="password-generator"
													element={
														<ProtectedRoute>
															<PasswordGenerator />
														</ProtectedRoute>
													}
												/>
												<Route
													path="agrian-issues"
													element={
														<ProtectedRoute>
															<AgrianIssues />
														</ProtectedRoute>
													}
												/>
												<Route
													path="post-sampling-issues"
													element={
														<ProtectedRoute>
															<PostSamplingIssues />
														</ProtectedRoute>
													}
												/>
												<Route
													path="unmatched-deals"
													element={
														<ProtectedRoute>
															<UnmatchedDeals />
														</ProtectedRoute>
													}
												/>
											</Route>
										</Routes>
									</div>
								</div>
							</CompanyContextProvider>
						</Router>
					</ThemeProvider>
				</UserContext.Provider>
				<ToastContainer position="bottom-left" />
			</div>
		</Fade>
	);
}

export default Sentry.withProfiler(App);
